/**
 * AI:
 * Status: "in progress"
 * Type: Component
 * SubType: DiagnosticPanel
 * Reason: Quick test for an lg.LogicMappingTable — resolves a source value through the production lookup path and reports value, resolution source, contract flags, table size and server-measured timings.
 */
import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {LogicMappingTableService} from '../../../service/api/logic-mapping-table.service';
import {LogicMappingTableDto} from '../../../model/dto/entity/logic-mapping-table.dto';
import {LogicMappingTestRequestDto} from '../../../model/dto/api/logic-mapping-test-request.dto';
import {LogicMappingTestResponseDto} from '../../../model/dto/api/logic-mapping-test-response.dto';
import {getLogicMappingResolutionSourceLabel} from '../../../enum/logic-mapping-resolution-source.enum';

/**
 * How the resolved value is to be rendered. The three states are deliberately distinct — collapsing
 * them would throw away exactly the distinction the backend refuses to blur.
 */
export type MappingTargetValueState = 'value' | 'emptyText' | 'absent';

/**
 * The severity values `p-tag` accepts, mirrored so the helper below can be bound with
 * `strictTemplates` on — a plain `string` return would not be assignable to the component's input.
 */
export type LgTagSeverity = 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast';

/**
 * Diagnostic panel for a single mapping table.
 *
 * Everything it shows comes from one `POST /mvsa/lg/logicMappingTables/{id}/testLookup` call, which
 * runs the same resolution path as production. Three properties of that response drive the rendering
 * and are easy to get wrong:
 *
 * 1. A lookup that finds nothing against a hard `ERROR` fallback is a BUSINESS result: HTTP 200 with
 *    `success = false`. It is rendered as a finding, not as a broken request.
 * 2. `targetValueIsAbsent` separates 'there is no value' from 'the value is an empty string'.
 * 3. `iterationsCompleted` can be lower than `iterationsRequested`. Every timing figure refers to the
 *    completed count, and the panel says so out loud rather than implying a full run.
 */
@Component({
    selector: 'mvs-lg-mapping-quick-test',
    templateUrl: './lg-mapping-quick-test.component.html',
    styleUrls: ['./lg-mapping-quick-test.component.scss'],
    standalone: false
})
export class LgMappingQuickTestComponent implements OnInit, OnChanges {

    /** Id of the mapping table to resolve against. */
    @Input() mappingTableId: number;

    /** The table itself, when the host already has it — used for the headline only. */
    @Input() mappingTableDto: LogicMappingTableDto;

    initialized: boolean = false;
    busy: boolean = false;

    // --- Form state ---
    sourceValue: string = '';
    partnerAlias: string = '';

    /**
     * Whether a caller default is supplied at all.
     *
     * This is not decoration: omitting `defaultValueOnNothing` and sending an empty string are two
     * DIFFERENT requests. Without the switch the panel could never produce the 'no default supplied'
     * case, which is the one where a hard `ERROR` fallback actually throws.
     */
    defaultValueProvided: boolean = false;
    defaultValueOnNothing: string = '';

    iterations: number = 1;

    readonly maxSourceValueLength: number = 500;
    readonly minIterations: number = 1;
    readonly maxIterations: number = 1000;

    // --- Result state ---
    result: LogicMappingTestResponseDto | null = null;

    /** Set only for a TECHNICAL failure (transport, 400, 500) — never for `success = false`. */
    technicalErrorMessage: string | null = null;

    /**
     * What the reported warm average does and does not contain. Shown with every multi-iteration run
     * because a figure that looks like production but is not would quietly justify skipping a cache.
     */
    readonly measurementNote: string =
        'Der Kaltwert ist der erste Durchlauf. Der Warm-Mittelwert entsteht mit geleertem ' +
        'Persistence-Context und misst damit den eingelaufenen Zustand von JVM und Datenbankpuffer — ' +
        'er misst KEINEN Applikations-Cache, denn einen solchen gibt es für Mapping-Tabellen nicht. ' +
        'Jeder Durchlauf erzeugt echte Datenbankzugriffe. Nicht enthalten sind HTTP-Weg, ' +
        'JSON-Verarbeitung, Berechtigungsprüfung und der Mehraufwand der Logic-Ausführung.';

    constructor(protected mappingTableService: LogicMappingTableService) {
    }

    ngOnInit(): void {
        this.initialized = true;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }

        if (changes['mappingTableId']) {
            this.clearResult();
        }
    }

    /** True while the mandatory source value is missing or too long for any entry to match. */
    isRunDisabled(): boolean {
        return this.busy
            || !this.mappingTableId
            || !this.sourceValue
            || this.sourceValue.length > this.maxSourceValueLength
            || !this.isIterationsValid();
    }

    isIterationsValid(): boolean {
        return this.iterations >= this.minIterations && this.iterations <= this.maxIterations;
    }

    /** Runs the lookup. Read-only on the server, so no confirmation is needed. */
    runTest(): void {
        if (this.isRunDisabled()) {
            return;
        }

        this.busy = true;
        this.result = null;
        this.technicalErrorMessage = null;

        const request: LogicMappingTestRequestDto = {
            sourceValue: this.sourceValue,
            iterations: this.iterations
        };

        if (this.partnerAlias) {
            request.partnerAlias = this.partnerAlias;
        }

        // Only send the field when the switch says a default IS supplied — an empty string here is a
        // supplied default, and normalising it away would silently change what is being tested.
        if (this.defaultValueProvided) {
            request.defaultValueOnNothing = this.defaultValueOnNothing ?? '';
        }

        this.mappingTableService.testLookup(this.mappingTableId, request).subscribe({
            next: (response: LogicMappingTestResponseDto) => {
                this.result = response;
                this.busy = false;
            },
            error: (error) => {
                this.technicalErrorMessage = error?.error?.message
                    ?? 'Der Schnelltest konnte nicht ausgeführt werden.';
                this.busy = false;
            }
        });
    }

    clearResult(): void {
        this.result = null;
        this.technicalErrorMessage = null;
    }

    /**
     * Which of the three target-value states applies.
     *
     * `absent` and `emptyText` look identical in a naive rendering and mean opposite things: one is
     * 'nothing matched and the table deliberately produced no value', the other is 'an entry matched
     * and its target value happens to be empty'.
     */
    getTargetValueState(): MappingTargetValueState {
        if (!this.result) {
            return 'absent';
        }
        if (this.result.targetValueIsAbsent) {
            return 'absent';
        }
        if (this.result.targetValue === '' || this.result.targetValue === null || this.result.targetValue === undefined) {
            return 'emptyText';
        }
        return 'value';
    }

    /** The placeholder text for the two non-value states. */
    getTargetValuePlaceholder(): string {
        return this.getTargetValueState() === 'absent' ? '(kein Wert)' : '(leerer Text)';
    }

    /** German label of the resolution source as it arrived from the backend. */
    getResolutionSourceLabel(): string {
        return getLogicMappingResolutionSourceLabel(this.result?.resolutionSource);
    }

    /**
     * Severity of the resolution-source tag.
     *
     * The slow paths — a full miss falling through to the table default or to a fallback logic — are
     * the ones a configurator needs to spot at a glance, so they are not rendered like a hit.
     */
    getResolutionSourceSeverity(): LgTagSeverity {
        // Compared against the member NAME, which is what travels on the wire — comparing against the
        // numeric enum member would compare a string to a number and never match.
        const source: string | undefined = this.result?.resolutionSource;

        if (source === 'partnerOverride' || source === 'entry') {
            return 'success';
        }
        if (source === 'fallbackNull') {
            return 'secondary';
        }
        return 'warn';
    }

    /**
     * Formats a microsecond figure as German milliseconds with two decimals, e.g. `3,42 ms`.
     *
     * @param micros duration in microseconds, or null/undefined when the figure does not exist
     */
    formatMicros(micros: number | null | undefined): string {
        if (micros === null || micros === undefined) {
            return '—';
        }
        const millis: number = micros / 1000;
        return `${millis.toFixed(2).replace('.', ',')} ms`;
    }

    /** True when the server stopped the run early. */
    isBudgetExceeded(): boolean {
        return this.result?.budgetExceeded === true;
    }

    /**
     * The honest iteration line, e.g. `180 von 1000 Durchläufen — Zeitbudget erreicht`.
     *
     * A result that reads like a full run when it was cut short is a wrong measurement, so the
     * shortfall is spelled out rather than left to the reader to notice.
     */
    getIterationsText(): string {
        if (!this.result) {
            return '';
        }
        if (this.isBudgetExceeded()) {
            return `${this.result.iterationsCompleted} von ${this.result.iterationsRequested} `
                + `Durchläufen — Zeitbudget erreicht`;
        }
        return `${this.result.iterationsCompleted} `
            + `${this.result.iterationsCompleted === 1 ? 'Durchlauf' : 'Durchläufe'}`;
    }

    /** True when timing spread figures are meaningful (more than one completed iteration). */
    hasMultipleIterations(): boolean {
        return (this.result?.iterationsCompleted ?? 0) > 1;
    }

    /** Headline of the panel — the table name when the host handed it over. */
    getTableTitle(): string {
        if (!this.mappingTableDto) {
            return 'Schnelltest';
        }
        return this.mappingTableDto.alias
            ? `Schnelltest — ${this.mappingTableDto.name} (${this.mappingTableDto.alias})`
            : `Schnelltest — ${this.mappingTableDto.name}`;
    }
}
